import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Truck, Package, MapPin, Weight, Ruler, AlertTriangle, 
  Snowflake, Box, ArrowRight, Clock, Calculator
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const LogisticsHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [step, setStep] = useState(1); // 1: cargo details, 2: locations, 3: quote
  
  const [formData, setFormData] = useState({
    // Cargo details
    description: '',
    weight_kg: '',
    length_cm: '',
    width_cm: '',
    height_cm: '',
    category: 'general',
    special_instructions: '',
    // Locations
    pickup_address: '',
    dropoff_address: '',
    pickup_location: null,
    dropoff_location: null,
  });
  
  const [quotes, setQuotes] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [suggestedVehicle, setSuggestedVehicle] = useState(null);
  const [distanceKm, setDistanceKm] = useState(0);

  useEffect(() => {
    fetchVehiclesAndCategories();
  }, []);

  const fetchVehiclesAndCategories = async () => {
    try {
      const [vehiclesRes, categoriesRes] = await Promise.all([
        api.get('/logistics/vehicles'),
        api.get('/logistics/categories')
      ]);
      setVehicles(vehiclesRes.data.vehicles || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'fragile': return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'refrigerated': return <Snowflake className="h-5 w-5 text-blue-500" />;
      case 'hazardous': return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default: return <Box className="h-5 w-5 text-gray-500" />;
    }
  };

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'pickup_truck': return '🛻';
      case 'mini_truck': return '🚚';
      case 'large_truck': return '🚛';
      case 'cargo_van': return '🚐';
      default: return '🚛';
    }
  };

  const handleGetQuote = async () => {
    if (!formData.description || !formData.weight_kg) {
      toast({
        title: 'Missing Information',
        description: 'Please enter cargo description and weight',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.pickup_address || !formData.dropoff_address) {
      toast({
        title: 'Missing Locations',
        description: 'Please enter pickup and dropoff addresses',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // For demo, use approximate coordinates
      // In production, use Google Maps Geocoding
      const response = await api.post('/logistics/quote', {
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        pickup_location: formData.pickup_location || { latitude: -15.4167, longitude: 28.2833 },
        dropoff_location: formData.dropoff_location || { latitude: -15.5000, longitude: 28.3000 },
        cargo: {
          description: formData.description,
          weight_kg: parseFloat(formData.weight_kg),
          length_cm: formData.length_cm ? parseFloat(formData.length_cm) : null,
          width_cm: formData.width_cm ? parseFloat(formData.width_cm) : null,
          height_cm: formData.height_cm ? parseFloat(formData.height_cm) : null,
          category: formData.category,
          special_instructions: formData.special_instructions || null
        }
      });

      setQuotes(response.data.quotes || []);
      setSuggestedVehicle(response.data.suggested_vehicle);
      setDistanceKm(response.data.distance_km);
      setStep(3);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to get quote. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = (quote) => {
    setSelectedVehicle(quote);
    navigate('/logistics/book', { 
      state: { 
        quote, 
        formData,
        distanceKm 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Truck className="h-8 w-8" />
            <h1 className="text-2xl font-bold">Tekstar Logistics</h1>
          </div>
          <p className="text-orange-100">Transport your cargo safely and efficiently</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-1 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-8 text-sm text-gray-600 mb-6">
          <span className={step >= 1 ? 'text-orange-600 font-medium' : ''}>Cargo</span>
          <span className={step >= 2 ? 'text-orange-600 font-medium' : ''}>Locations</span>
          <span className={step >= 3 ? 'text-orange-600 font-medium' : ''}>Quote</span>
        </div>

        {/* Step 1: Cargo Details */}
        {step === 1 && (
          <Card className="p-6" data-testid="cargo-details-form">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-500" />
              Cargo Details
            </h2>

            <div className="space-y-4">
              <div>
                <Label>What are you shipping?</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Office furniture, electronics, building materials..."
                  className="mt-1"
                  data-testid="cargo-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Weight (kg) *</Label>
                  <div className="relative mt-1">
                    <Weight className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="number"
                      value={formData.weight_kg}
                      onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                      placeholder="500"
                      className="pl-10"
                      data-testid="cargo-weight"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Dimensions (optional)
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input
                    type="number"
                    value={formData.length_cm}
                    onChange={(e) => setFormData({ ...formData, length_cm: e.target.value })}
                    placeholder="Length (cm)"
                  />
                  <Input
                    type="number"
                    value={formData.width_cm}
                    onChange={(e) => setFormData({ ...formData, width_cm: e.target.value })}
                    placeholder="Width (cm)"
                  />
                  <Input
                    type="number"
                    value={formData.height_cm}
                    onChange={(e) => setFormData({ ...formData, height_cm: e.target.value })}
                    placeholder="Height (cm)"
                  />
                </div>
              </div>

              <div>
                <Label>Cargo Category</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setFormData({ ...formData, category: cat.id })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.category === cat.id
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-200'
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {getCategoryIcon(cat.id)}
                        <span className="font-medium">{cat.name}</span>
                        {cat.surcharge_percent > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded">
                            +{cat.surcharge_percent}%
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{cat.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Special Instructions (optional)</Label>
                <Textarea
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                  placeholder="Any special handling requirements..."
                  className="mt-1"
                />
              </div>

              <Button 
                onClick={() => setStep(2)} 
                className="w-full bg-orange-500 hover:bg-orange-600"
                disabled={!formData.description || !formData.weight_kg}
                data-testid="next-to-locations"
              >
                Continue to Locations
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {/* Step 2: Locations */}
        {step === 2 && (
          <Card className="p-6" data-testid="locations-form">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-orange-500" />
              Pickup & Delivery Locations
            </h2>

            <div className="space-y-4">
              <div>
                <Label>Pickup Address *</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-green-500" />
                  <Input
                    value={formData.pickup_address}
                    onChange={(e) => setFormData({ ...formData, pickup_address: e.target.value })}
                    placeholder="Enter pickup address"
                    className="pl-10"
                    data-testid="pickup-address"
                  />
                </div>
              </div>

              <div>
                <Label>Delivery Address *</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-red-500" />
                  <Input
                    value={formData.dropoff_address}
                    onChange={(e) => setFormData({ ...formData, dropoff_address: e.target.value })}
                    placeholder="Enter delivery address"
                    className="pl-10"
                    data-testid="dropoff-address"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleGetQuote} 
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  disabled={loading || !formData.pickup_address || !formData.dropoff_address}
                  data-testid="get-quote-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Getting Quote...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Get Quote
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Quote Results */}
        {step === 3 && (
          <div className="space-y-4" data-testid="quote-results">
            <Card className="p-4 bg-orange-50 border-orange-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Distance</p>
                  <p className="text-xl font-bold text-orange-600">{distanceKm.toFixed(1)} km</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Cargo Weight</p>
                  <p className="text-xl font-bold">{formData.weight_kg} kg</p>
                </div>
              </div>
            </Card>

            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Truck className="h-5 w-5 text-orange-500" />
              Select a Vehicle
            </h2>

            <div className="space-y-3">
              {quotes.map((quote) => (
                <Card 
                  key={quote.vehicle_type}
                  className={`p-4 cursor-pointer transition-all ${
                    quote.is_recommended 
                      ? 'border-2 border-orange-500 bg-orange-50' 
                      : 'hover:border-orange-200'
                  }`}
                  onClick={() => handleSelectVehicle(quote)}
                  data-testid={`vehicle-${quote.vehicle_type}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{getVehicleIcon(quote.vehicle_type)}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{quote.vehicle_name}</h3>
                          {quote.is_recommended && (
                            <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                              Recommended
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {quote.estimated_duration}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-orange-600">
                        K{quote.total_price.toFixed(2)}
                      </p>
                      <button 
                        className="text-xs text-orange-500 underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Show breakdown
                        }}
                      >
                        View breakdown
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <Button 
              variant="outline" 
              onClick={() => setStep(2)}
              className="w-full"
            >
              Back to Locations
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogisticsHome;
